import { useEffect, useMemo, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { formatPrice } from "@/lib/currency";
import { ArrowRight, Info } from "lucide-react";

export const FEATURE_OPTIONS = [
  { id: "extra-pages", label: "Extra pages (up to 4)", price: 50, hint: "About, services, contact, etc." },
  { id: "custom-design", label: "Custom-designed pages", price: 75, hint: "No off-the-shelf templates." },
  { id: "contact-form", label: "Contact form with email notifications", price: 50 },
  { id: "blog-cms", label: "Blog / news section (you can edit it)", price: 100 },
  { id: "seo", label: "SEO setup + Google indexing", price: 50 },
  { id: "animations", label: "Subtle animations & polish", price: 75 },
  { id: "booking", label: "Booking / scheduling integration", price: 100 },
  { id: "ecommerce", label: "Mini shop (up to 5 products)", price: 150 },
  { id: "multi-language", label: "Multi-language (2 languages)", price: 75 },
  { id: "copywriting", label: "Copywriting assistance", price: 50 },
];

const BASE_GBP = 150;
const CAP_GBP = 500;

export default function Calculator({
  currency,
  rates,
  selectedFeatures,
  setSelectedFeatures,
  estimateGbp,
  setEstimateGbp,
}) {
  const [pop, setPop] = useState(false);

  const subtotal = useMemo(() => {
    const sum =
      BASE_GBP +
      FEATURE_OPTIONS.filter((f) => selectedFeatures.includes(f.id)).reduce(
        (acc, f) => acc + f.price,
        0
      );
    return Math.min(sum, CAP_GBP);
  }, [selectedFeatures]);

  const uncapped = useMemo(() => {
    return (
      BASE_GBP +
      FEATURE_OPTIONS.filter((f) => selectedFeatures.includes(f.id)).reduce(
        (acc, f) => acc + f.price,
        0
      )
    );
  }, [selectedFeatures]);

  const capped = uncapped > CAP_GBP;

  useEffect(() => {
    setEstimateGbp(subtotal);
    setPop(true);
    const t = setTimeout(() => setPop(false), 320);
    return () => clearTimeout(t);
  }, [subtotal, setEstimateGbp]);

  const toggle = (id) => {
    setSelectedFeatures((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const scrollToInquiry = () =>
    document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" });

  const progress = Math.min(((subtotal - BASE_GBP) / (CAP_GBP - BASE_GBP)) * 100, 100);

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="border-t border-[#E5E0D8] bg-[#F3EFE9]"
    >
      <div className="oak-container oak-section">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-start">
          {/* Left: features */}
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-[#5C665F]">Build your quote</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight mt-3 text-[#1A3622]">
              Tick what you need.
              <br />
              See the price instantly.
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-[#5C665F] max-w-xl">
              Every site starts at <strong className="text-[#1A3622]">{formatPrice(BASE_GBP, currency, rates)}</strong>
              {" "}(a polished one-pager). Add only the pieces you actually need.
            </p>

            <div data-testid="calculator-features" className="mt-8 grid sm:grid-cols-2 gap-3">
              {FEATURE_OPTIONS.map((f) => {
                const checked = selectedFeatures.includes(f.id);
                return (
                  <label
                    key={f.id}
                    data-testid={`feature-${f.id}`}
                    htmlFor={`feat-${f.id}`}
                    className={[
                      "flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-all",
                      checked
                        ? "border-[#1A3622] bg-white shadow-sm"
                        : "border-[#E5E0D8] bg-white/70 hover:bg-white hover:border-[#C8C2B7]",
                    ].join(" ")}
                  >
                    <Checkbox
                      id={`feat-${f.id}`}
                      data-testid={`calc-feature-checkbox-${f.id}`}
                      checked={checked}
                      onCheckedChange={() => toggle(f.id)}
                      className="mt-0.5 border-[#1A3622] data-[state=checked]:bg-[#1A3622] data-[state=checked]:text-white"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-3">
                        <Label
                          htmlFor={`feat-${f.id}`}
                          className="font-body font-medium text-[15px] text-[#1C211E] cursor-pointer"
                        >
                          {f.label}
                        </Label>
                        <span className="font-heading font-bold text-sm text-[#1A3622] whitespace-nowrap">
                          +{formatPrice(f.price, currency, rates)}
                        </span>
                      </div>
                      {f.hint && (
                        <p className="text-[13px] text-[#5C665F] mt-1">{f.hint}</p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Right: sticky total */}
          <aside className="lg:sticky lg:top-28">
            <div
              data-testid="calculator-total-card"
              className="relative overflow-hidden rounded-2xl border border-[#E5E0D8] bg-white p-7 md:p-9 shadow-lg"
            >
              <p className="text-sm uppercase tracking-[0.18em] text-[#5C665F]">Your estimate</p>

              <div className="mt-3 flex items-baseline gap-3">
                <span
                  key={subtotal}
                  data-testid="calculator-total"
                  className={
                    "font-heading font-extrabold tracking-tight text-[#1A3622] text-5xl md:text-6xl " +
                    (pop ? "oak-total-pop" : "")
                  }
                >
                  {formatPrice(subtotal, currency, rates)}
                </span>
                {currency !== "GBP" && (
                  <span
                    data-testid="calculator-total-gbp"
                    className="text-sm text-[#5C665F] font-body"
                  >
                    ≈ {formatPrice(subtotal, "GBP", rates)}
                  </span>
                )}
              </div>

              <p className="mt-2 text-sm text-[#5C665F]">One-time build cost. Quoted in writing before any work starts.</p>

              {/* Progress between min and cap */}
              <div className="mt-6">
                <div className="flex justify-between text-xs text-[#5C665F] font-body">
                  <span>{formatPrice(BASE_GBP, currency, rates)}</span>
                  <span>{formatPrice(CAP_GBP, currency, rates)}</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-[#F3EFE9] overflow-hidden">
                  <div
                    data-testid="calculator-progress"
                    className="h-full bg-[#1A3622] transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {capped && (
                <div
                  data-testid="calculator-cap-notice"
                  className="mt-5 flex items-start gap-2 rounded-lg bg-[#F3EFE9] border border-[#E5E0D8] px-3 py-2 text-[13px] text-[#1A3622]"
                >
                  <Info className="w-4 h-4 mt-0.5 shrink-0" />
                  Looks like a chunky project! Builds are capped at {formatPrice(CAP_GBP, currency, rates)} — we’ll happily quote anything bigger separately.
                </div>
              )}

              <hr className="my-6 border-[#E5E0D8]" />

              <ul className="space-y-2 text-[14px] text-[#5C665F] font-body">
                <li>Includes a fixed-price quote in writing</li>
                <li>Includes 1 round of design revisions</li>
                <li>Hosting + domain guidance included</li>
              </ul>

              <button
                onClick={scrollToInquiry}
                data-testid="calculator-cta"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1A3622] px-6 h-12 text-base font-medium text-white transition-all hover:-translate-y-0.5 hover:shadow-lg hover:bg-[#122618]"
              >
                Send me a quote
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
